import { Http } from "@Services";
import {
    IncomingMessageProps,
    IOutgoingMessageProps,
} from "@Interfaces/Entities/Report";
import {
    IVisualizationChart,
    ITotalNumbersStats,
} from "@Interfaces/Entities/Channels";

type RawVotesFormat = "csv" | "json";

export interface AiUsageResponse {
    used_year: number;
    remaining_year: number;
    total_year: number;
    used_month: number;
    monthly_reference: number;
    contract_year_start: string;
    contract_year_end: string;
    current_month: string;
    limit_reached: boolean;
}

export interface ChannelSummaryResponse {
    success: boolean;
    channel_id?: string;
    title?: string;
    executive_summary?: string;
    report_generated_at?: string | null;
    short_summary?: Record<string, unknown> | null;
    visualization_charts?: IVisualizationChart[];
    total_numbers_stats?: ITotalNumbersStats | null;
    error?: string;
}

export interface ChannelContextAvailabilityResponse {
    success: boolean;
    channel_id?: string;
    title?: string;
    has_analysis_bundle: boolean;
}

export const ReportService = {
    getChannelSummary: async (
        channelId: string
    ): Promise<ChannelSummaryResponse> => {
        let response: ChannelSummaryResponse;

        try {
            response = await Http.Request<ChannelSummaryResponse>({
                method: "GET",
                url: `/reports/channel/${channelId}/summary`,
            });
        } catch (error) {
            // @ts-ignore
            throw Error(error.statusText);
        }

        return response;
    },
    getChannelContextAvailability: async (
        channelId: string
    ): Promise<ChannelContextAvailabilityResponse> => {
        let response: ChannelContextAvailabilityResponse;

        try {
            response = await Http.Request<ChannelContextAvailabilityResponse>({
                method: "GET",
                url: `/reports/channel/${channelId}/context-availability`,
            });
        } catch (error) {
            // @ts-ignore
            throw Error(error.statusText);
        }

        return response;
    },
    postChatMessage: async (
        data: IOutgoingMessageProps
    ): Promise<IncomingMessageProps> => {
        const { channelId, message } = data;
        let response: any;
        try {
            response = await Http.Request<any>({
                method: "POST",
                url: `/assistant/channel/${channelId}/chat`,
                data: { message },
            });
        } catch (error) {
            // @ts-ignore
            throw Error(error.statusText);
        }

        return response;
    },
    postChannelReport: async (
        channelId: string,
        aiContext?: string
    ): Promise<{ status: string; job_id?: string }> => {
        try {
            return await Http.Request<{ status: string; job_id?: string }>({
                method: "POST",
                url: `/reports/channel`,
                data: {
                    channel_id: channelId,
                    ai_context: aiContext?.trim() || undefined,
                },
            });
        } catch (error) {
            // @ts-ignore
            throw Error(error.statusText);
        }
    },
    postPollReport: async (data: { poll_id: string }): Promise<any> => {
        let response: any;
        try {
            response = await Http.Request<any>({
                method: "POST",
                url: `/reports`,
                data,
            });
        } catch (error) {
            // @ts-ignore
            throw Error(error.statusText);
        }

        return response;
    },
    getPollReport: async (poll_id: string): Promise<any> => {
        let response: any;

        try {
            response = await Http.Request<any>({
                method: "GET",
                url: `/reports/${poll_id}`,
            });
        } catch (error) {
            // @ts-ignore
            throw Error(error.statusText);
        }

        return response;
    },
    getChannelRawVotes: async (
        channelId: string,
        format: RawVotesFormat = "csv"
    ): Promise<Blob> => {
        try {
            return await Http.Request<Blob>({
                method: "GET",
                url: `/groups/${channelId}/polls/votes/raw`,
                params: {
                    format,
                    ...(format === "csv" ? { view: "respondent_table" } : {}),
                    download: 1,
                },
                responseType: "blob",
            });
        } catch (error) {
            // @ts-ignore
            throw Error(error.statusText);
        }
    },
    getPollRawVotes: async (
        pollId: string,
        format: RawVotesFormat
    ): Promise<Blob> => {
        try {
            return await Http.Request<Blob>({
                method: "GET",
                url: `/polls/${pollId}/votes/raw`,
                params: { format },
                responseType: "blob",
            });
        } catch (error) {
            // @ts-ignore
            throw Error(error.statusText);
        }
    },
    getTextFieldsRaw: async (
        channelId: string,
        format: RawVotesFormat = "csv"
    ): Promise<Blob> => {
        try {
            return await Http.Request<Blob>({
                method: "GET",
                url: `/polls/${channelId}/textfields/raw`,
                params: { format, download: 1 },
                responseType: "blob",
            });
        } catch (error) {
            // @ts-ignore
            throw Error(error.statusText);
        }
    },
    getAiUsage: async (): Promise<AiUsageResponse> => {
        let response: AiUsageResponse;

        try {
            response = await Http.Request<AiUsageResponse>({
                method: "GET",
                url: "/assistant/usage",
            });
        } catch (error) {
            // @ts-ignore
            throw Error(error.statusText);
        }

        return response;
    },
};
